import { InfoOutlined } from "@mui/icons-material"
import {
  alpha,
  Box,
  Button,
  Chip,
  colors,
  LinearProgress,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  useMediaQuery,
  Theme,
  Typography,
} from "@mui/material"

import { useInfiniteQuery } from "@tanstack/react-query"

import { type Swap, getAllSwaps } from "../../api/swaps-api"
import { IdBlock } from "../../components/IdBlock"

import { LoadingSkeletons } from "../../components/LoadingSkeletons"
import { TokenAmountBlock } from "../../components/TokenAmountBlock"

import { TokenAmountInCurrency } from "../../components/TokenAmountInCurrency"
import { formatFullDate, formatRelative } from "../../utils/date-utils"
import { EntityBlock } from "@/components/EntityBlock"
import { InMemoryTable } from "@/components/InMemoryTable"
import { usePool } from "@/context/PoolContext"

export function SwapsTable() {
  const { pool, isReversed, isFlipped, baseToken, quoteToken, baseTokenInfo, quoteTokenInfo } =
    usePool()

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"))
  const PAGE_SIZE = isMobile ? 20 : 50

  const fetchSwaps = async ({ pageParam }: { pageParam?: string }) => {
    const result = await getAllSwaps(PAGE_SIZE, pageParam, false, "ingestedAt", pool.id)
    return result
  }

  const { data, isLoading, isRefetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["getAllSwaps", pool.id],
      queryFn: fetchSwaps,
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        const lastSwap = lastPage[lastPage.length - 1]
        return lastSwap ? lastSwap.cursor : undefined
      },
    })

  if (isLoading || !data) {
    return <LoadingSkeletons />
  }

  const swaps = data.pages.flat()

  if (isMobile) {
    return (
      <>
        {isRefetching && (
          <LinearProgress color="primary" variant="indeterminate" sx={{ height: 2 }} />
        )}
        {swaps.map((swap) => {
          const {
            fromToken,
            fromQuantity,
            toQuantity,
            from,
            id,
            ingestedAt,
            reservesA,
            reservesB,
            ref,
            tokenA,
            tokenB,
            tokenAPrice,
            tokenBPrice,
          } = swap

          const isBuyOrder = fromToken === quoteToken
          const baseTokenAmount = isBuyOrder ? toQuantity : fromQuantity
          const quoteTokenAmount = isBuyOrder ? fromQuantity : toQuantity

          // Determine which token is the quote token and use its USD price
          let usdPrice: number | undefined
          if (tokenA === quoteToken) {
            usdPrice = tokenAPrice
          } else if (tokenB === quoteToken) {
            usdPrice = tokenBPrice
          }

          let price = reservesA / reservesB

          if (isFlipped) {
            price = reservesB / reservesA
            if (isReversed) {
              price = reservesA / reservesB
            }
          } else {
            if (isReversed) {
              price = reservesB / reservesA
            }
          }
          if (baseTokenInfo && quoteTokenInfo) {
            price = price * 10 ** (baseTokenInfo.denom - quoteTokenInfo.denom)
          }

          const color = isBuyOrder ? colors.green[500] : colors.red[500]

          return (
            <TableRow hover key={id}>
              <TableCell colSpan={8}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={isBuyOrder ? "Buy" : "Sell"}
                      sx={{
                        borderRadius: 1,
                        color: color,
                        background: alpha(color, 0.1),
                        fontWeight: "inherit",
                      }}
                    />
                    <Tooltip title={formatFullDate(new Date(ingestedAt))}>
                      <Typography variant="body2">
                        {formatRelative(new Date(ingestedAt))}
                      </Typography>
                    </Tooltip>
                    <Typography variant="body2" color="text.secondary">
                      Ref: {ref}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="body2">
                      Price:{" "}
                      <TokenAmountInCurrency
                        amount={price}
                        hideCopyToClipboard
                        needsParsing={false}
                        quoteTokenUsdPrice={usdPrice}
                        tokenId={quoteToken}
                      />
                    </Typography>
                    <Typography variant="body2">
                      {baseTokenInfo?.ticker}:{" "}
                      <TokenAmountBlock
                        amount={baseTokenAmount}
                        needsParsing
                        tokenId={baseToken}
                        hideCopyToClipboard
                      />
                    </Typography>
                    <Typography variant="body2">
                      {quoteTokenInfo?.ticker}:{" "}
                      <TokenAmountInCurrency
                        amount={quoteTokenAmount}
                        quoteTokenUsdPrice={usdPrice}
                        tokenId={quoteToken}
                      />
                    </Typography>
                  </Stack>

                  <Stack direction="row" gap={2} alignItems="center">
                    <Stack direction="row" gap={0.5} alignItems="center">
                      <Typography variant="body2">From:</Typography>
                      <EntityBlock
                        entityId={from}
                        poolId={pool.id}
                        quoteToken={quoteToken}
                        baseToken={baseToken}
                      />
                    </Stack>
                    <IdBlock label="Tx" value={id} href={`https://ao.link/#/message/${id}`} />
                  </Stack>
                </Stack>
              </TableCell>
            </TableRow>
          )
        })}

        {hasNextPage && (
          <Button onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? "Loading more..." : "Load More"}
          </Button>
        )}
      </>
    )
  }

  return (
    <>
      {isRefetching && (
        <LinearProgress color="primary" variant="indeterminate" sx={{ height: 2 }} />
      )}
      <InMemoryTable
        size="small"
        sx={{
          background: "var(--mui-palette-paperAlt)",
          "& .MuiTableBody-root": {
            maxHeight: 500, // TODO
            overflowY: "auto",
          },
        }}
        data={swaps}
        headerCells={[
          {
            field: "ingestedAt" satisfies keyof Swap,
            label: (
              <Stack direction="row" gap={0.5} alignItems="center">
                Seen at
                <Tooltip title="The UTC timestamp when the transaction was first detected by the gateway.">
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              </Stack>
            ),
            sx: { width: 160 },
            // sortable: true,
          },
          {
            field: "ref" satisfies keyof Swap,
            label: (
              <Stack direction="row" gap={0.5} alignItems="center">
                Sequence Id
                <Tooltip title="The unique ordering ID for the transaction/message within the liquidity pool process.">
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              </Stack>
            ),
            sx: { width: 140 },
            align: "right",
          },
          { label: "Type", sx: { width: 140 } },
          {
            label: "Price",
            align: "right",
          },
          {
            label: baseTokenInfo?.ticker,
            align: "right",
          },
          {
            label: quoteTokenInfo?.ticker,
            align: "right",
          },
          { label: "From", sx: { width: 220 } },
          { label: "Tx", sx: { width: 140 } },
        ]}
        initialSortDir="desc"
        initialSortField={"ref" satisfies keyof Swap}
        pageSize={PAGE_SIZE}
        renderRow={(row: Swap) => {
          const {
            fromToken,
            fromQuantity,
            toQuantity,
            from,
            id,
            ingestedAt,
            reservesA,
            reservesB,
            tokenA,
            tokenB,
            tokenAPrice,
            tokenBPrice,
          } = row

          const isBuyOrder = fromToken === quoteToken
          const baseTokenAmount = isBuyOrder ? toQuantity : fromQuantity
          const quoteTokenAmount = isBuyOrder ? fromQuantity : toQuantity

          // Determine which token is the quote token and use its USD price
          let usdPrice: number | undefined
          if (tokenA === quoteToken) {
            usdPrice = tokenAPrice
          } else if (tokenB === quoteToken) {
            usdPrice = tokenBPrice
          }

          let price = reservesA / reservesB

          if (isFlipped) {
            price = reservesB / reservesA

            if (isReversed) {
              price = reservesA / reservesB
            }
          } else {
            if (isReversed) {
              price = reservesB / reservesA
            }
          }

          if (baseTokenInfo && quoteTokenInfo) {
            price = price * 10 ** (baseTokenInfo.denom - quoteTokenInfo.denom)
          }

          const color = isBuyOrder ? colors.green[500] : colors.red[500]

          return (
            <TableRow
              hover
              key={id}
              sx={{
                "& .MuiTableCell-root": {
                  color,
                },
              }}
            >
              <TableCell variant="text">
                <Tooltip title={formatFullDate(new Date(ingestedAt))}>
                  <span>{formatRelative(new Date(ingestedAt))}</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right" variant="number">
                {row.ref}
              </TableCell>
              <TableCell variant="text">
                <Chip
                  label={isBuyOrder ? "Buy" : "Sell"}
                  sx={{
                    borderRadius: 1,
                    color: color,
                    background: alpha(color, 0.1),
                    fontWeight: "inherit",
                    fontSize: "inherit",
                  }}
                />
              </TableCell>
              <TableCell align="right" variant="number">
                <TokenAmountInCurrency
                  amount={price}
                  hideCopyToClipboard
                  needsParsing={false}
                  quoteTokenUsdPrice={usdPrice}
                  tokenId={quoteToken}
                />
              </TableCell>
              <TableCell align="right" variant="number">
                <TokenAmountBlock
                  amount={baseTokenAmount}
                  needsParsing
                  tokenId={baseToken}
                  hideCopyToClipboard
                />
              </TableCell>
              <TableCell align="right" variant="number">
                <Box>
                  <TokenAmountInCurrency
                    amount={quoteTokenAmount}
                    quoteTokenUsdPrice={usdPrice}
                    tokenId={quoteToken}
                  />
                </Box>
              </TableCell>
              <TableCell padding="none" variant="text">
                <Box
                  sx={{
                    padding: (theme) => theme.spacing(0.75, 2),
                  }}
                >
                  <EntityBlock
                    entityId={from}
                    poolId={pool.id}
                    quoteToken={quoteToken as string}
                    baseToken={baseToken as string}
                  />
                </Box>
              </TableCell>
              <TableCell variant="text">
                <IdBlock label="View Tx" value={id} href={`https://ao.link/#/message/${id}`} />
              </TableCell>
            </TableRow>
          )
        }}
      />
      {hasNextPage && (
        <Button onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
          {isFetchingNextPage ? "Loading more..." : "Load More"}
        </Button>
      )}
    </>
  )
}
